// src/components/ServerManagement.js
//
// 🔄 المرحلة الأخيرة (GoTrue-login-cutover، 2026-07-06): إدارة السيرفرات بقت
// بتتعامل مع Postgres (daf_confirmation.system_config) عبر GET/POST
// /api/super-admin/servers (utils/serverDistribution.js) بدل Firestore
// مباشرة (`doc(db,'system_config','servers')`). الحفظ/الحذف/الإضافة كلهم
// بيعملوا full-replace write واحد (`updateServers`) — نفس دلالة `setDoc`
// القديمة بدون merge.
import React, { useState, useEffect } from 'react';
import { getAllServersStats, getServersList, updateServers } from '../utils/serverDistribution';
import './ServerManagement.css';

function ServerManagement() {
  const [servers, setServers] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingServer, setEditingServer] = useState(null);
  const [editValues, setEditValues] = useState({});

  useEffect(() => {
    loadServersData();
  }, []);

  const loadServersData = async () => {
    try {
      setLoading(true);

      // جلب الإحصائيات
      const statsData = await getAllServersStats();
      setStats(statsData);

      // جلب إعدادات السيرفرات
      const serversList = await getServersList();
      if (serversList.length > 0) {
        setServers(serversList);
      } else {
        // إذا لم تكن موجودة، استخدم القيم الافتراضية
        const defaultServers = [
          { id: 'S1', name: 'السيرفر الرئيسي', ip: '161.97.177.178', port: 3001, domain: 'api-daf.dafagents.com', maxClients: 10, priority: 1 },
        ];
        setServers(defaultServers);
        // حفظ القيم الافتراضية
        await updateServers(defaultServers);
      }

    } catch (error) {
      console.error('Error loading servers:', error);
      alert('خطأ في تحميل بيانات السيرفرات');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (server) => {
    setEditingServer(server.id);
    setEditValues({
      maxClients: server.maxClients,
      name: server.name,
      ip: server.ip,
      port: server.port,
      domain: server.domain
    });
  };

  const cancelEdit = () => {
    setEditingServer(null);
    setEditValues({});
  };

  const saveEdit = async (serverId) => {
    try {
      const updatedServers = servers.map(s => 
        s.id === serverId 
          ? { ...s, ...editValues }
          : s
      );
      
      await updateServers(updatedServers);
      setServers(updatedServers);
      setEditingServer(null);
      setEditValues({});
      
      alert('✅ تم حفظ التغييرات بنجاح!');
      
      // إعادة تحميل الإحصائيات
      const statsData = await getAllServersStats();
      setStats(statsData);
      
    } catch (error) {
      console.error('Error saving server:', error);
      alert('❌ خطأ في حفظ التغييرات');
    }
  };

  const deleteServer = async (serverId) => {
    if (!window.confirm(`هل أنت متأكد من حذف السيرفر ${serverId}؟`)) {
      return;
    }
    
    try {
      const updatedServers = servers.filter(s => s.id !== serverId);
      await updateServers(updatedServers);
      setServers(updatedServers);
      alert('✅ تم حذف السيرفر بنجاح!');
    } catch (error) {
      console.error('Error deleting server:', error);
      alert('❌ خطأ في حذف السيرفر');
    }
  };

  const addNewServer = async () => {
    const newServerId = prompt('أدخل معرف السيرفر الجديد (مثال: S3):');
    if (!newServerId) return;
    
    if (servers.find(s => s.id === newServerId)) {
      alert('❌ هذا المعرف موجود بالفعل!');
      return;
    }
    
    const newServer = {
      id: newServerId,
      name: `سيرفر إضافي ${servers.length}`,
      ip: prompt('أدخل IP السيرفر:') || '',
      port: parseInt(prompt('أدخل Port السيرفر:') || '3003'),
      domain: prompt('أدخل Domain السيرفر:') || '',
      maxClients: parseInt(prompt('أدخل السعة القصوى للعملاء:') || '15'),
      priority: servers.length + 1
    };
    
    try {
      const updatedServers = [...servers, newServer];
      await updateServers(updatedServers);
      setServers(updatedServers);
      alert('✅ تم إضافة السيرفر بنجاح!');
      
      // إعادة تحميل الإحصائيات
      const statsData = await getAllServersStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error adding server:', error);
      alert('❌ خطأ في إضافة السيرفر');
    }
  };

  const getServerStats = (serverId) => {
    return stats.find(s => s.id === serverId) || {
      activeClients: 0,
      usagePercent: 0,
      status: 'غير متصل'
    };
  };

  if (loading) {
    return <div className="server-mgmt-loading">جاري تحميل إعدادات السيرفرات...</div>;
  }

  const totalCapacity = servers.reduce((sum, s) => sum + s.maxClients, 0);
  const totalActive = stats.reduce((sum, s) => sum + s.activeClients, 0);
  const totalAvailable = totalCapacity - totalActive;
  const overallUsage = totalCapacity > 0 ? Math.round((totalActive / totalCapacity) * 100) : 0;

  return (
    <div className="server-management-container">
      <div className="server-mgmt-header">
        <h2>⚙️ إدارة السيرفرات</h2>
        <button onClick={addNewServer} className="add-server-btn">
          ➕ إضافة سيرفر جديد
        </button>
      </div>

      {/* ملخص عام */}
      <div className="server-summary-cards">
        <div className="summary-card">
          <div className="summary-icon">🖥️</div>
          <div className="summary-content">
            <div className="summary-label">إجمالي السيرفرات</div>
            <div className="summary-value">{servers.length}</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">👥</div>
          <div className="summary-content">
            <div className="summary-label">العملاء النشطين</div>
            <div className="summary-value">{totalActive}</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">📊</div>
          <div className="summary-content">
            <div className="summary-label">السعة الإجمالية</div>
            <div className="summary-value">{totalCapacity}</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">✨</div>
          <div className="summary-content">
            <div className="summary-label">الأماكن المتاحة</div>
            <div className="summary-value available">{totalAvailable}</div>
          </div>
        </div>

        <div className="summary-card full-width">
          <div className="summary-icon">📈</div>
          <div className="summary-content">
            <div className="summary-label">نسبة الاستخدام الكلية</div>
            <div className="usage-bar-container">
              <div className="usage-bar-fill" style={{ width: `${overallUsage}%` }}></div>
            </div>
            <div className="summary-value">{overallUsage}%</div>
          </div>
        </div>
      </div>

      {/* جدول السيرفرات */}
      <div className="servers-table-container">
        <table className="servers-table">
          <thead>
            <tr>
              <th>المعرف</th>
              <th>الاسم</th>
              <th>IP</th>
              <th>Port</th>
              <th>Domain</th>
              <th>السعة القصوى</th>
              <th>العملاء النشطين</th>
              <th>الحالة</th>
              <th>الاستخدام</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {servers.map(server => {
              const serverStats = getServerStats(server.id);
              const isEditing = editingServer === server.id;
              
              return (
                <tr key={server.id} className={`server-row ${serverStats.status.toLowerCase().replace(' ', '-')}`}>
                  <td><strong>{server.id}</strong></td>
                  
                  <td>
                    {isEditing ? (
                      <input 
                        type="text" 
                        value={editValues.name} 
                        onChange={(e) => setEditValues({...editValues, name: e.target.value})}
                        className="edit-input"
                      />
                    ) : server.name}
                  </td>
                  
                  <td>
                    {isEditing ? (
                      <input 
                        type="text" 
                        value={editValues.ip} 
                        onChange={(e) => setEditValues({...editValues, ip: e.target.value})}
                        className="edit-input"
                      />
                    ) : server.ip}
                  </td>
                  
                  <td>
                    {isEditing ? (
                      <input 
                        type="number" 
                        value={editValues.port} 
                        onChange={(e) => setEditValues({...editValues, port: parseInt(e.target.value)})}
                        className="edit-input small"
                      />
                    ) : server.port}
                  </td>
                  
                  <td className="domain-cell">
                    {isEditing ? (
                      <input 
                        type="text" 
                        value={editValues.domain} 
                        onChange={(e) => setEditValues({...editValues, domain: e.target.value})}
                        className="edit-input"
                      />
                    ) : server.domain}
                  </td>
                  
                  <td>
                    {isEditing ? (
                      <input 
                        type="number" 
                        value={editValues.maxClients} 
                        onChange={(e) => setEditValues({...editValues, maxClients: parseInt(e.target.value)})}
                        className="edit-input small"
                        min="1"
                        max="100"
                      />
                    ) : server.maxClients}
                  </td>
                  
                  <td><strong>{serverStats.activeClients}</strong></td>
                  
                  <td>
                    <span className={`status-badge ${serverStats.status === 'متاح' ? 'available' : serverStats.status === 'شبه ممتلئ' ? 'warning' : 'full'}`}>
                      {serverStats.status}
                    </span>
                  </td>
                  
                  <td>
                    <div className="mini-usage-bar">
                      <div 
                        className="mini-usage-fill" 
                        style={{ 
                          width: `${serverStats.usagePercent}%`,
                          backgroundColor: serverStats.usagePercent >= 90 ? '#dc3545' : 
                                         serverStats.usagePercent >= 70 ? '#ffc107' : '#28a745'
                        }}
                      ></div>
                    </div>
                    <span className="usage-text">{serverStats.usagePercent}%</span>
                  </td>
                  
                  <td>
                    {isEditing ? (
                      <div className="action-buttons">
                        <button onClick={() => saveEdit(server.id)} className="btn-save">💾 حفظ</button>
                        <button onClick={cancelEdit} className="btn-cancel">❌ إلغاء</button>
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <button onClick={() => startEdit(server)} className="btn-edit">✏️ تعديل</button>
                        {servers.length > 1 && (
                          <button onClick={() => deleteServer(server.id)} className="btn-delete">🗑️ حذف</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="server-mgmt-footer">
        <button onClick={loadServersData} className="refresh-btn">
          🔄 تحديث البيانات
        </button>
        <p className="footer-note">
          💡 نصيحة: احتفظ بـ 20% على الأقل من السعة متاحة لاستيعاب عملاء جدد
        </p>
      </div>
    </div>
  );
}

export default ServerManagement;
